// BevakningarPage — the candidate's saved-search manager ("Bevakningar"), a tab
// alongside the job board. Bevakningar 2.0: named saved searches the candidate
// creates, names, edits and deletes here; each fires an in-app notification when
// a newly published tjänst matches. "Visa tjänster" jumps back to the board with
// the search applied.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellPlus, Pencil, Trash2, Search } from 'lucide-react';
import { needSubscriptionApi } from '@/lib/api/needSubscriptionApi';
import { NeedSubscription, subscriptionDisplayName } from '@/lib/models/needSubscription';
import { criteriaChips } from '@/lib/jobMatching';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import { ConfirmDialog } from '@/components/custom-ui/ConfirmDialog';
import { NEXILINK_SPACING, NEXILINK_CARD_WHITE, NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import BevakningEditorModal from './BevakningEditorModal';

const BevakningarPage: React.FC = () => {
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState<NeedSubscription[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    // null = closed; { existing: null } = create; { existing: sub } = edit.
    const [editor, setEditor] = useState<{ existing: NeedSubscription | null } | null>(null);
    const [pendingDelete, setPendingDelete] = useState<NeedSubscription | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setIsLoading(true);
        try {
            setSubscriptions(await needSubscriptionApi.listMySubscriptions());
        } finally {
            setIsLoading(false);
        }
    };

    // Merge a created/updated bevakning into the list (upsert by id) and close.
    const handleSaved = (saved: NeedSubscription) => {
        setSubscriptions((prev) => {
            const exists = prev.some((s) => s.id === saved.id);
            return exists ? prev.map((s) => (s.id === saved.id ? saved : s)) : [saved, ...prev];
        });
        setEditor(null);
    };

    const handleDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        const target = pendingDelete;
        setSubscriptions((prev) => prev.filter((s) => s.id !== target.id)); // optimistic
        try {
            await needSubscriptionApi.deleteSubscription(target.id);
        } catch {
            load(); // revert to server truth on failure
        } finally {
            setDeleting(false);
            setPendingDelete(null);
        }
    };

    // Jump to the job board with this saved search applied (query + filters).
    const showMatchingJobs = (s: NeedSubscription) => {
        navigate('/dashboard/jobs', { state: { applySearch: { query: s.query, criteria: s.criteria ?? {} } } });
    };

    if (isLoading) {
        return <LoadingState message="Laddar dina bevakningar..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {/* Header */}
            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-5 flex items-center justify-between gap-4`}>
                <div>
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Bell className="w-5 h-5 text-[#1A4D4F]" /> Mina bevakningar
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Få en notis så snart en ny tjänst matchar din sökning.
                    </p>
                </div>
                <ActionButton icon={BellPlus} onClick={() => setEditor({ existing: null })}>
                    Ny bevakning
                </ActionButton>
            </div>

            {/* List / empty state */}
            {subscriptions.length === 0 ? (
                <div className={`${NEXILINK_CARD_WHITE} p-12 text-center`}>
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <Bell className="w-8 h-8 text-[#1A4D4F]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga bevakningar än</h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                        Skapa en bevakning för de tjänster du är intresserad av — så hör vi av oss
                        så snart något nytt matchar.
                    </p>
                    <button
                        onClick={() => setEditor({ existing: null })}
                        className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#1A4D4F] text-white rounded-xl text-sm font-medium hover:bg-[#143c3e]"
                    >
                        <BellPlus className="w-4 h-4" /> Skapa din första bevakning
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {subscriptions.map((s) => {
                        const chips = criteriaChips(s.query, s.criteria);
                        return (
                            <div key={s.id} className={`${NEXILINK_CARD_WHITE} p-5 flex flex-col`}>
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-semibold text-gray-900 leading-snug">
                                        {subscriptionDisplayName(s)}
                                    </h3>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => setEditor({ existing: s })}
                                            title="Redigera bevakning"
                                            className="p-2 rounded-lg text-gray-400 hover:text-[#1A4D4F] hover:bg-gray-100"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setPendingDelete(s)}
                                            title="Ta bort bevakning"
                                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {chips.map((c, i) => (
                                        <span
                                            key={`${c}-${i}`}
                                            className="inline-flex items-center rounded-full bg-[#A4C3B2]/25 px-2.5 py-1 text-xs font-medium text-[#1A4D4F]"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>

                                <button
                                    onClick={() => showMatchingJobs(s)}
                                    className="mt-4 inline-flex items-center gap-1.5 self-start text-sm font-medium text-[#1A4D4F] hover:underline underline-offset-2"
                                >
                                    <Search className="w-4 h-4" /> Visa matchande tjänster
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {editor && (
                <BevakningEditorModal
                    existing={editor.existing}
                    onClose={() => setEditor(null)}
                    onSaved={handleSaved}
                />
            )}

            <ConfirmDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && setPendingDelete(null)}
                title="Ta bort bevakning"
                description={
                    pendingDelete ? (
                        <>
                            Vill du ta bort bevakningen{' '}
                            <strong>{subscriptionDisplayName(pendingDelete)}</strong>? Du får då inga
                            fler notiser om matchande tjänster.
                        </>
                    ) : undefined
                }
                confirmLabel="Ta bort"
                destructive
                isBusy={deleting}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default BevakningarPage;
